import { loadScript } from './loadScript'

/** WeTech 官方 SDK（S3） */
export const WETECH_SDK_SCRIPT =
  'https://wetech-prod.s3.us-west-1.amazonaws.com/opendoc/wetech-sdk.min.js'

interface WetechInitResult {
  code: number
  msg?: string
}

interface WetechTokenResult {
  code?: number
  msg?: string
  err?: string
  data?: {
    device_id?: string
  } | null
}

export interface WetechDevicePayload {
  /** 成功时有值；失败为空串，不附带错误信息 */
  device_id: string
}

declare global {
  interface Window {
    _WetechID?: {
      init: (
        cfg: {
          apiUrl: string
          app_id: string
          secret_key: string
          client_info?: { channel?: string }
        },
        cb: (res: WetechInitResult) => void
      ) => void
      getDeviceToken: (cb: (res: WetechTokenResult) => void) => void
    }
  }
}

let initPromise: Promise<boolean> | null = null
let cachedDeviceId = ''

const getWetechConfig = () => ({
  apiUrl: (process.env.VUE_APP_WETECH_API_URL || '').trim(),
  appId: (process.env.VUE_APP_WETECH_APP_ID || '').trim(),
  secretKey: (process.env.VUE_APP_WETECH_SECRET_KEY || '').trim(),
  channel: (process.env.VUE_APP_WETECH_CHANNEL || 'test').trim()
})

const maskSecret = (secret: string) => {
  if (!secret) return ''
  if (secret.length <= 8) return '****'
  return `${secret.slice(0, 4)}****${secret.slice(-4)}`
}

/**
 * 全局初始化 WeTech SDK（同页只执行一次）。
 * 未配置 env 时直接返回 false，不影响主流程。
 */
export const ensureWetechInit = (): Promise<boolean> => {
  if (initPromise) {
    return initPromise
  }

  const { apiUrl, appId, secretKey, channel } = getWetechConfig()

  if (!apiUrl || !appId || !secretKey) {
    initPromise = Promise.resolve(false)
    return initPromise
  }
  initPromise = loadScript(WETECH_SDK_SCRIPT)
    .then(
      () =>
        new Promise<boolean>((resolve) => {
          const sdk = window._WetechID
          if (!sdk || typeof sdk.init !== 'function') {
            resolve(false)
            return
          }
          sdk.init(
            {
              apiUrl,
              app_id: appId,
              secret_key: secretKey,
              client_info: { channel }
            },
            (res) => {
              if (res?.code === 0) {
                resolve(true)
                return
              }
              resolve(false)
            }
          )
        })
    )
    .catch((e) => {
      return false
    })

  return initPromise
}

/**
 * 获取设备指纹 Token（device_id）；失败返回空串，不阻断支付。
 */
export const getWetechDeviceToken = async (): Promise<string> => {
  const payload = await getWetechDevicePayload()
  return payload.device_id
}

/**
 * 设备指纹上报载荷：
 * - 成功：device_id 有值
 * - 失败：device_id 为空串（不传错误信息给后端）
 */
export const getWetechDevicePayload = async (): Promise<WetechDevicePayload> => {
  if (cachedDeviceId) {
    return { device_id: cachedDeviceId }
  }
  const ok = await ensureWetechInit()
  if (!ok || !window._WetechID) {
    return { device_id: '' }
  }

  return new Promise((resolve) => {
    try {
      window._WetechID!.getDeviceToken((res) => {
        const id = String(res?.data?.device_id || '').trim()
        if (id) {
          cachedDeviceId = id
          resolve({ device_id: id })
          return
        }
        resolve({ device_id: '' })
      })
    } catch (e) {
      console.warn('[wetech] getDeviceToken error', e)
      resolve({ device_id: '' })
    }
  })
}
